import React, { useState, useEffect } from 'react'
import { Skeleton, Stack } from '@mui/material'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { TrainingSprintStepProps, TrainingPurpose, ActivityType } from '../types'
import { FACILITATOR_COLORS } from '../../../../constants/colors'
import { SprintPurpose } from '../../../../shared/components/Purpose'
import { createActivityInstanceFromTemplate } from '../utils/activityConfiguration'
import { useSprintWizardConfig } from '../hooks/useSprintWizardConfig'

const buildActivitiesForPurpose = (
  purpose: string,
  orderedTypesByPurpose: Record<string, Array<{ type: ActivityType; isFixed?: boolean }>>,
  t: (key: string, defaultValue?: string) => string
) => {
  const orderedTypes = orderedTypesByPurpose[purpose] ?? []

  return orderedTypes
    .map(({ type, isFixed }) => createActivityInstanceFromTemplate(type, t, { isFixed: isFixed ?? false }))
    .filter((activity): activity is NonNullable<typeof activity> => Boolean(activity))
}

export const TrainignSpringDefinePurposeStepOne: React.FC<TrainingSprintStepProps> = ({ data, updateData }) => {
  const { t } = useTranslation()
  const tCompat = t as (key: string, defaultValue?: string) => string
  const [selectedPurpose, setSelectedPurpose] = useState<TrainingPurpose | undefined>(data.purpose)
  const { purposes, orderedTypesByPurpose, isLoading } = useSprintWizardConfig()

  // Update data when purpose changes
  useEffect(() => {
    // Skip activity generation while config is loading to avoid overwriting with fallback data
    if (isLoading) return

    // Generate activities if:
    // 1. Purpose has changed, OR
    // 2. Purpose is set but activities are empty (initial load case)
    if (selectedPurpose && (selectedPurpose !== data.purpose || data.activities.length === 0)) {
      // Only regenerate when purpose actually changes (not when activities change)
      // Generate ONLY activities (sessions are created from activities by the backend)
      const activities = buildActivitiesForPurpose(selectedPurpose, orderedTypesByPurpose, tCompat)

      updateData('purpose', selectedPurpose)
      updateData('activities', activities)
    }
  }, [selectedPurpose, data.purpose, data.activities.length, updateData, t, isLoading, orderedTypesByPurpose])

  // NOTE: Sessions are NO LONGER generated here!
  // The backend creates sessions from the activities automatically.
  // We only generate and manage activities in the frontend.

  const handlePurposeSelect = (purposeId: TrainingPurpose) => {
    setSelectedPurpose(purposeId)
  }

  const handlePurposeSelectWrapper = (purposeId: string) => {
    handlePurposeSelect(purposeId as TrainingPurpose)
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-4" style={{ color: FACILITATOR_COLORS.facilitatorWizardTextWhite }}>
          {t('facilitator.trainingSprint.definePurpose.title')}
        </h2>
        <p className="text-lg" style={{ color: FACILITATOR_COLORS.facilitatorWizardText }}>
          {t('facilitator.trainingSprint.definePurpose.subtitle')}
        </p>
      </div>

      {/* Purpose Selection using the new component - NO TITLE/DESCRIPTION FIELDS */}
      {isLoading ? (
        <Stack direction={{ xs: 'column', sm: 'column', md: 'row' }} spacing={2}>
          <Skeleton variant="rounded" width="100%" height={220} />
          <Skeleton variant="rounded" width="100%" height={220} />
          <Skeleton variant="rounded" width="100%" height={220} />
        </Stack>
      ) : (
        <SprintPurpose
          purposes={purposes}
          selectedPurpose={selectedPurpose || ''}
          onPurposeSelect={handlePurposeSelectWrapper}
        />
      )}
    </div>
  )
}
